import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from 'react';

/**
 * FontContext centralizes the state every specimen row needs to read:
 *   - customText: the sample string being previewed across all fonts
 *   - fontSize:   the shared preview size in px (12–120)
 *   - theme:      'light' | 'dark'
 *
 * Keeping this in context (rather than prop-drilling through the
 * virtualized list) means adding new consumers never requires touching
 * the components in between.
 */
const FontContext = createContext(null);

const DEFAULT_TEXT = 'Широкая электрификация южных губерний даст мощный толчок подъёму сельского хозяйства.';
const MIN_SIZE = 12;
const MAX_SIZE = 120;
const DEFAULT_SIZE = 42;
const THEME_STORAGE_KEY = 'font-previewer:theme';

export function FontProvider({ children }) {
  const [customText, setCustomText] = useState(DEFAULT_TEXT);
  const [fontSize, setFontSize] = useState(DEFAULT_SIZE);
  const [searchTerm, setSearchTerm] = useState('');

  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return 'light';
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  });

  // Toggle the `.dark` class on <html> so Tailwind's `dark:` variants apply
  // app-wide, and persist the choice.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', theme === 'dark');
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // localStorage can throw in private-browsing/sandboxed contexts —
      // theme just won't persist, which is a fine fallback.
    }
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((t) => (t === 'light' ? 'dark' : 'light'));
  }, []);

  const clampFontSize = useCallback((value) => {
    return Math.min(MAX_SIZE, Math.max(MIN_SIZE, value));
  }, []);

  const value = useMemo(
    () => ({
      customText,
      setCustomText,
      fontSize,
      setFontSize: (v) => setFontSize(clampFontSize(v)),
      theme,
      toggleTheme,
      MIN_SIZE,
      MAX_SIZE,
      searchTerm,
      setSearchTerm,
    }),
    [customText, fontSize, theme, toggleTheme, clampFontSize, searchTerm]
  );

  return <FontContext.Provider value={value}>{children}</FontContext.Provider>;
}

export function useFontContext() {
  const ctx = useContext(FontContext);
  if (!ctx) {
    throw new Error('useFontContext must be used within a <FontProvider>');
  }
  return ctx;
}
